<template>
    <div>
        <form @submit.prevent="saveSettings" v-if="config">
            <div v-if="isCreditEnabled">
                <h2 class="mb-4">{{ $t('tipSettings') }}</h2>
                <div class="row mb-3">
                    <div class="col">
                        <label for="tipOption1" class="form-label">{{ $t('tipOption1') }}</label>
                        <input type="number" class="form-control" id="tipOption1" v-model="settings.tip.options[0]" min="0"
                            max="100">
                        <div v-if="settings.tip.options[0] < 0 || settings.tip.options[0] > 100" class="text-danger">
                            {{ $t('tipOptionRangeError') }}
                        </div>
                    </div>
                    <div class="col">
                        <label for="tipOption2" class="form-label">{{ $t('tipOption2') }}</label>
                        <input type="number" class="form-control" id="tipOption2" v-model="settings.tip.options[1]" min="0"
                            max="100">
                        <div v-if="settings.tip.options[1] < 0 || settings.tip.options[1] > 100" class="text-danger">
                            {{ $t('tipOptionRangeError') }}
                        </div>
                    </div>
                    <div class="col">
                        <label for="tipOption3" class="form-label">{{ $t('tipOption3') }}</label>
                        <input type="number" class="form-control" id="tipOption3" v-model="settings.tip.options[2]" min="0"
                            max="100">
                        <div v-if="settings.tip.options[2] < 0 || settings.tip.options[2] > 100" class="text-danger">
                            {{ $t('tipOptionRangeError') }}
                        </div>
                    </div>
                </div>
                <div class="mb-3">
                    <label for="defaultTip" class="form-label">{{ $t('defaultTip') }} (%)</label>
                    <select class="form-select" id="defaultTip" v-model="settings.tip.default"
                        :aria-describedby="'defaultTipHelp'">
                        <option v-for="(option, index) in settings.tip.options" :key="option" :value="index">{{ option }}%
                        </option>
                    </select>
                    <small id="defaultTipHelp" class="form-text text-muted">{{ $t('defaultTipPercentageHelp') }}</small>
                </div>
                <div class="mb-3">
                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="settings.tip.allowNoTip" id="allowNoTip">
                        <label class="form-check-label" for="allowNoTip">{{ $t('allowNoTip') }}</label>
                    </div>
                    <small class="form-text text-muted">{{ $t('allowNoTipHelp') }}</small>
                </div>
                <div class="mb-3">
                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="settings.tip.allowCustomTip"
                            id="allowCustomTip">
                        <label class="form-check-label" for="allowCustomTip">{{ $t('allowCustomTip') }}</label>
                    </div>
                    <small class="form-text text-muted">{{ $t('allowCustomTipHelp') }}</small>
                </div>
                <div class="mb-3">
                    <label for="tipCalculationMethod" class="form-label">{{ $t('tipCalculationMethod') }}</label>
                    <select class="form-select" id="tipCalculationMethod" v-model="settings.tip.calculationMethod">
                        <option value="includeTaxes">{{ $t('includeTaxesTip') }}</option>
                        <option value="excludeTaxes">{{ $t('excludeTaxesTip') }}</option>
                    </select>
                </div>
            </div>
            <template v-if="settings.rails === 'olo'">
                <h2 class="mt-5 mb-4">{{ $t('paymentTypes') }}</h2>
                <p class="text-muted mb-4">{{ $t('paymentTypesHelp') }}</p>
                <div v-for="(paymentType, index) in settings.paymentTypes" :key="index" class="card mb-4">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">{{ getPaymentTypeDisplayName(paymentType.type) }}</h5>
                        <span class="badge bg-secondary">{{ paymentType.type }}</span>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label :for="'name-' + index" class="form-label">{{ $t('displayName') }}</label>
                                    <input type="text" class="form-control" :id="'name-' + index" v-model="paymentType.name"
                                        :placeholder="$t('displayNamePlaceholder')">
                                    <small class="form-text text-muted">{{ $t('displayNameHelp') }}</small>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label :for="'icon-' + index" class="form-label">{{ $t('icon') }}</label>
                                    <div class="input-group">
                                        <select :id="'icon-' + index" v-model="paymentType.icon" class="form-select">
                                            <option value="">{{ $t('noIcon') }}</option>
                                            <option v-for="icon in paymentIcons" :key="icon" :value="icon">{{ camelToTitle(icon) }}</option>
                                        </select>
                                        <span class="input-group-text">
                                            <SvgIcon v-if="paymentType.icon" :name="paymentType.icon" width="2rem" height="2rem" />
                                        </span>
                                    </div>
                                    <small class="form-text text-muted">{{ $t('iconHelp') }}</small>
                                </div>
                            </div>
                        </div>
                        <div class="mb-3">
                            <label :for="'instructions-' + index" class="form-label">{{ $t('instructions') }}</label>
                            <textarea class="form-control" :id="'instructions-' + index" v-model="paymentType.instructions"
                                :placeholder="$t('instructionsPlaceholder')" rows="3"></textarea>
                            <small class="form-text text-muted">{{ $t('instructionsHelp') }}</small>
                        </div>
                        <div v-if="paymentType.icon" class="mb-3">
                            <label class="form-label">{{ $t('iconPreview') }}</label>
                            <div class="p-2 border rounded bg-light">
                                <SvgIcon :name="paymentType.icon" width="24" height="24" />
                                <span class="ms-2">{{ paymentType.name }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <save-button :isDirty="isSettingsDirty" @save="saveSettings" :stickyBottom="true"
                :serverDone="serverDone"></save-button>
        </form>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import SaveButton from '@/components/features/admin/SaveButton.vue';
import SvgIcon from '@/components/global/SvgIcon.vue';

export default {
    components: {
        SaveButton,
        SvgIcon
    },
    computed: {
        ...mapState('admin', ['config', 'isAdmin']),
        settings: {
            get() {
                return this.config || {};
            },
            set(value) {
                this.setConfig(value);
            }
        },
        isSettingsDirty() {
            return JSON.stringify(this.config) !== JSON.stringify(this.originalSettings);
        },
        isCreditEnabled() {
            if (this.settings && this.settings.paymentTypes && Array.isArray(this.settings.paymentTypes)) {
                return this.settings.paymentTypes.some(pt => pt.type === 'CREDIT');
            }
            return false;
        }
    },
    data() {
        return {
            serverDone: false,
            originalSettings: null,
            editingField: '',
            paymentIcons: [
                'pay-credit',
                'pay-up-front',
                'pay-custom',
                'credit-card',
                'cash',
                'badge',
                'employee',
                'user',
                'barcode',
                'barcodeScanner'
            ],
            defaultAdvantageProgramSettings: {
                enabled: false,
                type: '',
                priceAdjustmentPercentage: null,
                feePercentage: null,
                ticketDescription: '',
                discountPercentage: null
            }
        };
    },
    methods: {
        ...mapActions('admin', ['fetchConfig', 'saveConfig', 'setConfig']),
        isValidPercentage(value) {
            if (value === null || value === undefined) return true;
            const regex = /^\d+(\.\d{1,2})?$/;
            return regex.test(value);
        },
        validateSettings() {
            const { tip, advantageProgram } = this.settings;
            const validTipOptions = tip.options.every(option => option >= 0 && option <= 100);
            const validAdvantageProgram = !advantageProgram.enabled || (
                (advantageProgram.type === 'dualPricing' && this.isValidPercentage(advantageProgram.priceAdjustmentPercentage)) ||
                (advantageProgram.type === 'supplementalFee' && this.isValidPercentage(advantageProgram.feePercentage)) ||
                (advantageProgram.type === 'cashDiscount' && this.isValidPercentage(advantageProgram.discountPercentage))
            );
            return validTipOptions && validAdvantageProgram;
        },
        saveSettings() {
            if (!this.validateSettings()) {
                return;
            }

            this.serverDone = false;
            if (!this.config.advantageProgram) {
                this.$set(this.config, 'advantageProgram', this.defaultAdvantageProgramSettings);
            }
            this.saveConfig(this.config).then(() => {
                this.originalSettings = JSON.parse(JSON.stringify(this.config));
            })
            .finally(() => {
                this.serverDone = true;
            })
        },
        getPaymentTypeDisplayName(type) {
            const typeNames = {
                'CASH': this.$t('payUpFront'),
                'NON_CASH': this.$t('customPayment'),
                'CREDIT': this.$t('creditCard')
            };
            return typeNames[type] || type;
        },
        camelToTitle(str) {
            return str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
        }
    },
    async created() {
        await this.fetchConfig();
        if (!this.config.advantageProgram) {
            this.$set(this.config, 'advantageProgram', this.defaultAdvantageProgramSettings);
        }
        this.originalSettings = JSON.parse(JSON.stringify(this.config));
    }
};
</script>

<style scoped>
h2 {
    margin-top: 2rem;
}

.float {
    width: 8rem;
}

</style>
